import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessGroups } from "@/hooks/use-access-groups";
import {
  useDocumentFolderGrants,
  useSetDocumentFolderGrant,
  grantLevelOf,
  GRANT_LEVEL_LABELS,
  type GrantLevel,
} from "@/hooks/use-document-folder-grants";
import { useDocumentFolders } from "@/hooks/use-document-folders";
import { useUsers } from "@/hooks/use-users";
import {
  folderAccessList,
  folderChain,
  chainIsComplete,
  type FolderNode,
} from "@/lib/folder-access";
import { SYSTEM_ROLE_LABELS, type SystemRole } from "@/lib/constants";
import { FolderAccessList } from "./FolderAccessList";

const NIVAER: GrantLevel[] = ["none", "read", "write"];

/**
 * Den arkivspesifikke halvdelen: hvilke grupper en mappe er delt med, og hvem
 * som dermed har tilgang.
 *
 * Mappevelgeren viser hele stien, fordi tilgang til en undermappe avhenger av
 * kjeden oppover — «2026» alene sier ingenting om hvem som ser den.
 */
export function FolderAccessCard({ initialFolderId }: { initialFolderId: string | null }) {
  const { user: authUser } = useAuth();
  const { data: folders = [] } = useDocumentFolders();
  const { data: groups = [] } = useAccessGroups();
  const { data: grants = [] } = useDocumentFolderGrants();
  const { data: users = [] } = useUsers();
  const settTilgang = useSetDocumentFolderGrant();

  const [valgtId, setValgtId] = useState<string | null>(initialFolderId);
  const mappe = folders.find((f) => f.id === (valgtId ?? folders[0]?.id)) ?? null;

  const noder: FolderNode[] = useMemo(
    () =>
      folders.map((f) => ({
        id: f.id,
        name: f.name,
        parent_id: f.parent_id,
        read_min_role: f.read_min_role,
        write_min_role: f.write_min_role,
        deleted_at: f.deleted_at,
      })),
    [folders],
  );

  /** «Styreprotokoller / 2026» — forelderen først, som i en filsti. */
  function stiFor(folderId: string): string {
    return folderChain(folderId, noder)
      .slice()
      .reverse()
      .map((k) => k.name)
      .join(" / ");
  }

  const kjede = mappe ? folderChain(mappe.id, noder) : [];

  const rader = useMemo(
    () =>
      mappe
        ? folderAccessList({
            folderId: mappe.id,
            folders: noder,
            grants,
            groups: groups.map((g) => ({
              id: g.id,
              name: g.name,
              member_ids: g.member_ids,
            })),
            users: users.map((u) => ({
              id: u.id,
              full_name: u.full_name,
              role: u.role,
            })),
          })
        : [],
    [mappe, noder, grants, groups, users],
  );

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center gap-2">
          <SrIcon name="folder" className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-base">Mappetilgang</h2>
        </div>

        <div className="max-w-sm space-y-1.5">
          <Label htmlFor="mappevalg">Mappe</Label>
          <Select
            value={mappe?.id ?? ""}
            onValueChange={(v) => setValgtId(v)}
          >
            <SelectTrigger id="mappevalg">
              <SelectValue placeholder="Velg mappe" />
            </SelectTrigger>
            <SelectContent>
              {folders.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {stiFor(f.id)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!mappe ? (
          <p className="text-sm text-muted-foreground">Ingen mapper tilgjengelig.</p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{stiFor(mappe.id)}</span> kan leses
              av {SYSTEM_ROLE_LABELS[mappe.read_min_role as SystemRole]} og over,
              og endres av {SYSTEM_ROLE_LABELS[mappe.write_min_role as SystemRole]}{" "}
              og over. Gruppene under gir tilgang i tillegg til det.
            </p>

            <div className="space-y-2">
              <Label className="text-xs">Delt med grupper</Label>
              {groups.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Ingen grupper å dele med ennå.
                </p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {groups.map((g) => {
                    const niva = grantLevelOf(
                      grants.find(
                        (t) => t.folder_id === mappe.id && t.group_id === g.id,
                      ),
                    );
                    return (
                      <li
                        key={g.id}
                        className="flex flex-wrap items-center gap-2 px-3 py-2"
                      >
                        <span className="text-sm">{g.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {g.member_ids.length}{" "}
                          {g.member_ids.length === 1 ? "medlem" : "medlemmer"}
                        </span>
                        <Select
                          value={niva}
                          onValueChange={(v) => {
                            if (!authUser?.id) return;
                            settTilgang.mutate({
                              folderId: mappe.id,
                              groupId: g.id,
                              level: v as GrantLevel,
                              grantedBy: authUser.id,
                            });
                          }}
                        >
                          <SelectTrigger
                            className="ml-auto h-8 w-52"
                            aria-label={`Tilgang for ${g.name}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {NIVAER.map((n) => (
                              <SelectItem key={n} value={n}>
                                {GRANT_LEVEL_LABELS[n]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <FolderAccessList
              rows={rader}
              folderName={mappe.name}
              chainComplete={chainIsComplete(mappe.id, noder)}
              inheritedFrom={kjede.slice(1).map((k) => k.name)}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
